import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sparkles,
  Download,
  RefreshCw,
  Zap,
  Loader2,
  Infinity,
  ImagePlus,
  X,
  Crown,
} from "lucide-react";
import { useState, useEffect, useRef, type ChangeEvent } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription } from "@/hooks/useSubscription";
import { useGenerations } from "@/hooks/useGenerations";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import ProBadge from "@/components/dashboard/ProBadge";

interface GeneratedThumbnail {
  id: string;
  imageUrl: string;
  title: string;
  style: string;
  emotion: string;
}

const Dashboard = () => {
  const [videoTitle, setVideoTitle] = useState("");
  const [selectedStyle, setSelectedStyle] = useState("exciting");
  const [selectedEmotion, setSelectedEmotion] = useState("happy");
  const [isGenerating, setIsGenerating] = useState(false);
  const [thumbnails, setThumbnails] = useState<GeneratedThumbnail[]>([]);
  const [referenceImage, setReferenceImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const { user, loading } = useAuth();
  const { subscribed } = useSubscription();
  const { generationsLeft, useGeneration, loading: generationsLoading } = useGenerations();
  const navigate = useNavigate();
  
  // Premium users get unlimited generations
  const isUnlimited = subscribed;

  useEffect(() => {
    if (!loading && !user) {
      navigate("/login");
    }
  }, [user, loading, navigate]);

  const handleImageUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setReferenceImage(event.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const removeReferenceImage = () => {
    setReferenceImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };


  const handleGenerate = async () => {
    if (!videoTitle.trim()) {
      toast.error("Please enter a video title");
      return;
    }

    if (!isUnlimited && generationsLeft <= 0) {
      toast.error("No generations left. Please upgrade to Pro or wait 24 hours for reset!");
      return;
    }

    // For free users, try to use a generation
    if (!isUnlimited) {
      const canGenerate = await useGeneration();
      if (!canGenerate) {
        toast.error("No generations left. Please upgrade to Pro or wait 24 hours!");
        return;
      }
    }

    setIsGenerating(true);
    setThumbnails([]); // Clear previous thumbnails
    toast.info("Generating 4 thumbnails... This may take a moment.");

    try {
      const { data, error } = await supabase.functions.invoke('generate-thumbnail', {
        body: {
          title: videoTitle,
          style: selectedStyle,
          emotion: selectedEmotion,
          referenceImage: referenceImage || undefined,
        },
      });

      if (error) {
        console.error('Function error:', error);
        toast.error(error.message || 'Failed to generate thumbnails');
        return;
      }

      if (data.error) {
        toast.error(data.error);
        return;
      }

      // Handle multiple thumbnails
      if (data.imageUrls && Array.isArray(data.imageUrls)) {
        const newThumbnails: GeneratedThumbnail[] = [];
        
        for (const imageUrl of data.imageUrls) {
          let storedImageUrl = imageUrl;
          
          // Upload base64 image to storage
          try {
            const base64Match = imageUrl.match(/^data:image\/(\w+);base64,(.+)$/);
            if (base64Match && user?.id) {
              const imageType = base64Match[1];
              const base64Data = base64Match[2];
              
              // Convert base64 to Uint8Array
              const binaryString = atob(base64Data);
              const bytes = new Uint8Array(binaryString.length);
              for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
              }
              
              // Generate unique filename
              const fileName = `${user.id}/${Date.now()}-${crypto.randomUUID()}.${imageType}`;
              
              // Upload to storage
              const { data: uploadData, error: uploadError } = await supabase.storage
                .from('thumbnails')
                .upload(fileName, bytes, {
                  contentType: `image/${imageType}`,
                  upsert: false,
                });
              
              if (uploadError) {
                console.error('Storage upload error:', uploadError);
              } else if (uploadData) {
                const { data: signedUrlData, error: signedUrlError } = await supabase.storage
                  .from('thumbnails')
                  .createSignedUrl(uploadData.path, 60 * 60 * 24 * 365); // 1 year

                if (!signedUrlError && signedUrlData?.signedUrl) {
                  storedImageUrl = signedUrlData.signedUrl;
                }
              }
            }
          } catch (uploadErr) {
            console.error('Error uploading to storage:', uploadErr);
          }

          // Save to database
          const { data: savedThumbnail, error: saveError } = await supabase
            .from('thumbnails')
            .insert({
              user_id: user?.id,
              title: videoTitle,
              style: selectedStyle,
              emotion: selectedEmotion,
              image_url: storedImageUrl,
            })
            .select()
            .single();

          if (saveError) {
            console.error('Error saving thumbnail:', saveError);
          }

          newThumbnails.push({
            id: savedThumbnail?.id || crypto.randomUUID(),
            imageUrl: storedImageUrl,
            title: videoTitle,
            style: selectedStyle,
            emotion: selectedEmotion,
          });
        }
        
        setThumbnails(newThumbnails);
        toast.success(`${newThumbnails.length} thumbnails generated successfully!`);
      }
    } catch (err) {
      console.error('Error:', err);
      toast.error('Failed to generate thumbnails. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = async (thumbnail: GeneratedThumbnail) => {
    try {
      // Convert base64 to blob and download
      const response = await fetch(thumbnail.imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `thumbnail-${thumbnail.id}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("Thumbnail downloaded!");
    } catch (err) {
      toast.error("Failed to download thumbnail");
    }
  };

  const proStyles = ["mysterious", "dramatic"];
  const proEmotions = ["confident", "excited"];

  const styles = [
    { id: "exciting", name: "🔥 Exciting" },
    { id: "professional", name: "💼 Professional" },
    { id: "mysterious", name: "🌙 Mysterious" },
    { id: "educational", name: "📚 Educational" },
    { id: "fun", name: "🎉 Fun" },
    { id: "dramatic", name: "⚡ Dramatic" },
  ];

  const emotions = [
    { id: "happy", name: "😊 Happy" },
    { id: "shocked", name: "😱 Shocked" },
    { id: "curious", name: "🤔 Curious" },
    { id: "confident", name: "😎 Confident" },
    { id: "excited", name: "🤩 Excited" },
    { id: "serious", name: "😐 Serious" },
  ];


  if (loading || generationsLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <DashboardLayout activeTab="dashboard" generationsLeft={generationsLeft}>
        {/* Header */}
        <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
          <div className="flex items-center justify-between px-4 sm:px-6 py-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold">Create Thumbnail</h1>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Generate scroll-stopping thumbnails in seconds
              </p>
            </div>
            <div className="hidden lg:flex items-center gap-2">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-xl ${subscribed ? 'bg-green-500/10 border border-green-500/30' : 'bg-secondary'}`}>
                {subscribed ? (
                  <>
                    <Infinity className="w-4 h-4 text-green-500" />
                    <span className="text-sm font-medium text-green-400">Unlimited</span>
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4 text-primary" />
                    <span className="text-sm font-medium">{generationsLeft} generations left</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Promotional Banner - Shows when 1 generation left */}
        {!subscribed && generationsLeft === 1 && (
          <div className="mx-4 sm:mx-6 mt-4 sm:mt-6 p-4 rounded-xl bg-gradient-to-r from-primary/20 via-accent/20 to-primary/20 border border-primary/30">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-brand-purple to-brand-pink flex items-center justify-center">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Last generation remaining!</h3>
                  <p className="text-sm text-muted-foreground">Upgrade to Pro for unlimited thumbnails</p>
                </div>
              </div>
              <Link to="/pricing">
                <Button variant="gradient" size="sm" className="gap-2">
                  <Crown className="w-4 h-4" />
                  Upgrade Now
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-4 sm:p-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
            {/* Left - Input */}
            <div className="space-y-4 sm:space-y-6">
              {/* Video Title + Reference Image */}
              <div className="p-4 sm:p-6 rounded-2xl bg-card border border-border">
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium mb-3">
                      Video Title or Idea
                    </label>
                    <Input
                      placeholder="How I Made $100K with YouTube in 2024"
                      value={videoTitle}
                      onChange={(e) => setVideoTitle(e.target.value)}
                      className="h-14 text-lg"
                      disabled={isGenerating}
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      Describe your video to help AI understand the context
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-3">
                      Reference Image (Optional)
                    </label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={isGenerating}
                    />

                    {referenceImage ? (
                      <div className="relative">
                        <img
                          src={referenceImage}
                          alt="Reference"
                          className="w-full h-32 object-cover rounded-xl border border-border"
                        />
                        <button
                          onClick={removeReferenceImage}
                          className="absolute top-2 right-2 p-1 rounded-full bg-background/80 hover:bg-background border border-border"
                          disabled={isGenerating}
                          aria-label="Remove reference image"
                          type="button"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isGenerating}
                        className="w-full h-32 border-2 border-dashed border-border rounded-xl flex flex-col items-center justify-center gap-2 hover:border-primary/50 transition-colors disabled:opacity-50"
                        type="button"
                      >
                        <ImagePlus className="w-8 h-8 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                          Upload a reference image
                        </span>
                      </button>
                    )}

                    <p className="text-xs text-muted-foreground mt-2">
                      Upload an image to guide the AI for more precise results
                    </p>
                  </div>
                </div>
              </div>

              {/* Style Selection */}
              <div className="p-4 sm:p-6 rounded-2xl bg-card border border-border">
                <label className="block text-sm font-medium mb-3">
                  Choose Style
                </label>
                <div className="grid grid-cols-3 gap-2 sm:gap-3">
                  {styles.map((style) => (
                    <button
                      key={style.id}
                      onClick={() => setSelectedStyle(style.id)}
                      disabled={isGenerating}
                      className={`px-3 sm:px-4 py-3 sm:py-4 rounded-xl border text-xs sm:text-sm font-medium transition-all ${
                        selectedStyle === style.id
                          ? "border-primary bg-primary/10"
                          : "border-border hover:border-primary/50"
                      } disabled:opacity-50`}
                    >
                      {style.name}
                      {!subscribed && proStyles.includes(style.id) && <ProBadge />}
                    </button>
                  ))}
                </div>
              </div>

              {/* Emotion Selection */}
              <div className="p-4 sm:p-6 rounded-2xl bg-card border border-border">
                <label className="block text-sm font-medium mb-3">
                  Choose Emotion
                </label>
                <div className="grid grid-cols-3 gap-2 sm:gap-3">
                  {emotions.map((emotion) => (
                    <button
                      key={emotion.id}
                      onClick={() => setSelectedEmotion(emotion.id)}
                      disabled={isGenerating}
                      className={`px-3 sm:px-4 py-3 sm:py-4 rounded-xl border text-xs sm:text-sm font-medium transition-all ${
                        selectedEmotion === emotion.id
                          ? "border-primary bg-primary/10"
                          : "border-border hover:border-primary/50"
                      } disabled:opacity-50`}
                    >
                      {emotion.name}
                      {!subscribed && proEmotions.includes(emotion.id) && <ProBadge />}
                    </button>
                  ))}
                </div>
              </div>

              {/* Generate Button */}
              <Button 
                variant="hero" 
                size="xl" 
                className="w-full"
                onClick={handleGenerate}
                disabled={isGenerating || (!isUnlimited && generationsLeft <= 0)}
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Generate Thumbnails
                  </>
                )}
              </Button>
            </div>

            {/* Right - Preview */}
            <div className="space-y-4 sm:space-y-6">
              <div className="p-4 sm:p-6 rounded-2xl bg-card border border-border">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold">Generated Thumbnails</h3>
                  {thumbnails.length > 0 && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={handleGenerate}
                      disabled={isGenerating || (!isUnlimited && generationsLeft <= 0)}
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${isGenerating ? 'animate-spin' : ''}`} />
                      Regenerate
                    </Button>
                  )}
                </div>

                {/* Thumbnail Grid */}
                <div className="grid grid-cols-2 gap-4">
                  {thumbnails.length > 0 ? (
                    thumbnails.map((thumbnail) => (
                      <div
                        key={thumbnail.id}
                        className="group relative aspect-video rounded-xl border border-border overflow-hidden hover:border-primary/50 transition-all cursor-pointer"
                      >
                        <img 
                          src={thumbnail.imageUrl} 
                          alt={thumbnail.title}
                          className="w-full h-full object-cover"
                        />
                        {/* Hover Actions */}
                        <div className="absolute inset-0 bg-background/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <Button 
                            size="sm" 
                            variant="gradient"
                            onClick={() => handleDownload(thumbnail)}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  ) : (
                    // Placeholder thumbnails
                    [1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className="group relative aspect-video rounded-xl bg-gradient-to-br from-secondary to-muted border border-border overflow-hidden"
                      >
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-center">
                            <div className="w-12 h-12 rounded-xl gradient-bg flex items-center justify-center mx-auto mb-2">
                              <Sparkles className="w-6 h-6 text-primary-foreground" />
                            </div>
                            <span className="text-xs text-muted-foreground">
                              Thumbnail {i}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <p className="text-xs text-muted-foreground text-center mt-4">
                  {thumbnails.length > 0 
                    ? "Hover over a thumbnail to download"
                    : "Enter a title and click Generate to create thumbnails"
                  }
                </p>
              </div>

              {/* Tips */}
              <div className="p-4 rounded-xl bg-primary/5 border border-primary/20">
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-brand-purple" />
                  Pro Tips
                </h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Be specific with your video title for better results</li>
                  <li>• Try different style + emotion combinations</li>
                  <li>• Regenerate if you want fresh variations</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
    </DashboardLayout>
  );
};

export default Dashboard;
